import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Nav from "../../../components/Nav";
import PayWidgetEvents from "../../../components/PayWidgetEvents";
import { useTrackEvent } from "../../../hooks/useTrackEvent";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  clearCombineBundleCheckoutStorage,
  getCombineBundleSuccessMessage,
  registerAndPurchaseCombineBundle,
} from "../../../services/PaymentService";
import { registerAccountStore } from "../../../store/slices/accountSlice";
import CombineBundleOffer from "../../../components/combine-bundle/CombineBundleOffer";
import {
  ArrowLeftIcon,
  CheckCircle,
  CloseIcon,
} from "../../../components/combine-bundle/BundleIcons";
import {
  OPTION_BUNDLE,
  OPTION_SINGLE,
  OPTION_SINGLE_WITH_DRILL,
  formatMoney,
  getBundleId,
  getBundlePrice,
  getCombineDate,
  getCombineLocation,
  getCombineName,
  getCombinePrice,
  getCombines,
  getDrillCombineCount,
  getHasPositionDrills,
  getPositionDrillPrice,
  getTotalSeparatePrice,
  isBundleOfferUsable,
  resolveSelectedCombine,
} from "../../../components/combine-bundle/bundleHelpers";

const STEPS = ["Offers Details", "T-Shirt Size", "Position", "Combine Bundle"];
const ACTIVE_STEP = STEPS.length - 1;

const StepChevron = () => (
  <svg
    viewBox="0 0 16 80"
    preserveAspectRatio="none"
    className="h-full w-4 flex-shrink-0 text-gray-200"
    aria-hidden="true"
  >
    <path
      d="M0 0 L16 40 L0 80"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      vectorEffect="non-scaling-stroke"
    />
  </svg>
);

const Stepper = () => (
  <div className="flex items-stretch border border-gray-200 rounded-lg overflow-hidden h-16 sm:h-20">
    {STEPS.map((label, index) => {
      const isActive = index === ACTIVE_STEP;
      return (
        <div key={label} className="flex flex-1 items-stretch min-w-0">
          {index > 0 && <StepChevron />}
          <div
            className={`flex flex-1 items-center justify-center gap-2 sm:gap-3 px-2 min-w-0 ${
              isActive ? "bg-blue-50" : ""
            }`}
          >
            {isActive ? (
              <span className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0 rounded-full bg-blue-600 text-white font-bold text-base sm:text-lg flex items-center justify-center">
                {index + 1}
              </span>
            ) : (
              <CheckCircle className="h-7 w-7 sm:h-8 sm:w-8" />
            )}
            <span
              className={`text-sm truncate ${
                isActive
                  ? "font-semibold text-blue-600 hidden sm:inline"
                  : "text-gray-800 hidden md:inline"
              }`}
            >
              {label}
            </span>
          </div>
        </div>
      );
    })}
  </div>
);

const CombineBundleStep = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();

  const { eventId, bundle } = location.state || {};
  const combines = getCombines(bundle);
  const selectedCombine = resolveSelectedCombine(bundle, eventId);
  const selectedHasDrills = getHasPositionDrills(selectedCombine);

  const singlePrice = getCombinePrice(selectedCombine);
  const selectedDrillPrice = getPositionDrillPrice(selectedCombine);
  const singleWithDrillPrice = singlePrice + selectedDrillPrice;

  const [option, setOption] = useState(
    selectedHasDrills ? OPTION_SINGLE_WITH_DRILL : OPTION_SINGLE,
  );
  const [isIntellipayReady, setIsIntellipayReady] = useState(false);
  const [isRegisteringFree, setIsRegisteringFree] = useState(false);

  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();
  const accounts = useSelector((state) => state.account.register);
  const storedAccount = useMemo(
    () => JSON.parse(localStorage.getItem("registeredAccount") || "{}"),
    [],
  );
  const user = accounts.length ? accounts[0] : storedAccount;

  const isBundleOption = option === OPTION_BUNDLE;
  const bundleId = getBundleId(bundle);
  const bundlePrice = getBundlePrice(bundle);
  // Only the bundle is paid on this screen; the single-event options keep the
  // plans screen, where a coupon can be entered (bundles take no coupon).
  const payBundleHere = isBundleOption && bundlePrice > 0;
  // Handed to PayWidgetEvents; its presence is what switches the approval
  // handler to the bundle purchase.
  const bundleChoice = useMemo(
    () => ({ bundleId, bundlePrice, combines }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bundleId, bundlePrice, bundle],
  );

  useEffect(() => {
    if (!accounts.length && Object.keys(storedAccount).length > 0) {
      dispatch(registerAccountStore(storedAccount));
    }
  }, [dispatch, accounts.length, storedAccount]);

  useEffect(() => {
    if (!payBundleHere) setIsIntellipayReady(false);
  }, [payBundleHere]);

  // An unusable offer falls back to the unbundled flow as when there is no
  // bundle.
  const canShowOffer = isBundleOfferUsable(bundle, eventId);

  useEffect(() => {
    if (!canShowOffer) {
      navigate("/athlete-membership", {
        state: { fromSpotRegister: true },
        replace: true,
      });
    }
  }, [canShowOffer, navigate]);

  useEffect(() => {
    if (!canShowOffer) return;
    trackEvent("Highschool_ViewedCombineBundleUpsell", {
      event_id: eventId,
      bundle_id: getBundleId(bundle),
      bundle_price: getBundlePrice(bundle),
      total_separate_price: getTotalSeparatePrice(bundle),
      combine_count: combines.length,
      combines_with_position_drills: getDrillCombineCount(bundle),
      portal_type: "highschool",
    });
  }, [canShowOffer, bundle, eventId, combines.length, trackEvent]);

  if (!canShowOffer) return null;

  const handleDecline = (positionDrill) => {
    trackEvent("Highschool_DeclinedCombineBundle", {
      event_id: eventId,
      bundle_id: getBundleId(bundle),
      position_drill: positionDrill,
      portal_type: "highschool",
    });

    localStorage.removeItem("bundleDetails");

    trackEvent("Highschool_OpenedMembershipPlans", {
      event_id: eventId,
      portal_type: "highschool",
    });

    navigate("/athlete-membership", {
      state: { fromSpotRegister: true },
    });
  };

  // Bundles never carry a coupon, so any coupon keys are left over from an
  // abandoned attempt on the plans screen.
  const handleAccept = () => {
    trackEvent("Highschool_AcceptedCombineBundle", {
      event_id: eventId,
      bundle_id: bundleId,
      bundle_price: bundlePrice,
      portal_type: "highschool",
    });
    localStorage.removeItem("appliedCouponData");
    localStorage.removeItem("appliedCouponCode");
    localStorage.removeItem("appliedCouponType");
  };

  // A bundle price can reach zero (100% off, or an amount off at or above the
  // separate total), so the free bundle completes here without the widget.
  const handleFreeBundle = async () => {
    handleAccept();
    setIsRegisteringFree(true);
    try {
      await registerAndPurchaseCombineBundle(apiInstance, {
        user,
        eventId,
        bundle: bundleChoice,
      });
      trackEvent("Registration5_RegistrationCompleted", {
        event_id: eventId,
        bundle_id: bundleId,
        payment_method: "free",
        portal_type: "highschool",
      });
      toast.success(getCombineBundleSuccessMessage(bundleChoice), {
        position: "top-right",
      });
      clearCombineBundleCheckoutStorage();
      navigate("/register/registrationsuccess");
    } catch (error) {
      toast.error(error.message || "Combine bundle registration failed.", {
        position: "top-right",
      });
    } finally {
      setIsRegisteringFree(false);
    }
  };

  const handleContinue = () => {
    if (isBundleOption) {
      if (bundlePrice === 0) handleFreeBundle();
    } else {
      handleDecline(option === OPTION_SINGLE_WITH_DRILL);
    }
  };

  const handlePayBundleClick = (e) => {
    if (!isIntellipayReady) {
      e.preventDefault();
      toast.info("IntelliPay is loading… please wait.", {
        position: "top-right",
      });
      return;
    }
    handleAccept();
  };

  const continueLabel = {
    [OPTION_SINGLE_WITH_DRILL]: `Single Event + Position Drill – ${formatMoney(singleWithDrillPrice)}`,
    [OPTION_SINGLE]: `Single Event Only – ${formatMoney(singlePrice)}`,
    [OPTION_BUNDLE]: `Continue with Bundle – ${formatMoney(getBundlePrice(bundle))}`,
  }[option];

  return (
    <>
      <Nav />
      <div className="min-h-screen bg-[#363636] w-full px-4 py-8 sm:py-10">
        <div className="w-full max-w-6xl mx-auto bg-white text-gray-900 rounded-lg shadow-xl">
          {/* Header */}
          <div className="px-5 sm:px-10 pt-6 sm:pt-8">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-4xl font-bold text-gray-900">
                  Event Registration
                </h1>
                {selectedCombine && (
                  <p className="text-sm sm:text-base text-gray-600 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="uppercase">
                      {getCombineName(selectedCombine)}
                    </span>
                    {getCombineDate(selectedCombine) && (
                      <>
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                        <span>{getCombineDate(selectedCombine)}</span>
                      </>
                    )}
                    {getCombineLocation(selectedCombine) && (
                      <>
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                        <span>{getCombineLocation(selectedCombine)}</span>
                      </>
                    )}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-4 sm:gap-8 pt-1 sm:pt-3 flex-shrink-0">
                <span className="text-sm sm:text-base text-gray-800 whitespace-nowrap">
                  Step {ACTIVE_STEP + 1} of {STEPS.length}
                </span>
                <button
                  type="button"
                  onClick={() => handleDecline(false)}
                  title="Close"
                  aria-label="Close"
                  className="text-gray-700 hover:text-gray-900 cursor-pointer"
                >
                  <CloseIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="mt-5">
              <Stepper />
            </div>
          </div>

          {/* Body */}
          <CombineBundleOffer
            bundle={bundle}
            eventId={eventId}
            option={option}
            onOptionChange={setOption}
          />

          {/* Footer */}
          <div className="border-t border-gray-200 px-5 sm:px-10 py-5 flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="border-2 border-gray-700 text-gray-900 font-semibold rounded px-6 py-3 hover:bg-gray-50 cursor-pointer flex items-center justify-center gap-3"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              Previous
            </button>
            {payBundleHere ? (
              // IntelliPay binds its popup to the ipayfield submit element.
              // Keyed apart from the other button so switching options never
              // leaves the widget's listener on a button that should not pay.
              <button
                key="pay"
                type="button"
                data-ipayname="submit"
                onClick={handlePayBundleClick}
                disabled={!isIntellipayReady}
                aria-busy={!isIntellipayReady}
                className={`ipayfield text-white font-semibold rounded px-8 py-3.5 ${
                  isIntellipayReady
                    ? "bg-blue-700 hover:bg-blue-800 cursor-pointer"
                    : "bg-gray-500 cursor-not-allowed opacity-70"
                }`}
              >
                {isIntellipayReady ? continueLabel : "Loading…"}
              </button>
            ) : (
              <button
                key="continue"
                type="button"
                onClick={handleContinue}
                disabled={isRegisteringFree}
                className={`text-white font-semibold rounded px-8 py-3.5 ${
                  isRegisteringFree
                    ? "bg-gray-500 cursor-not-allowed"
                    : "bg-blue-700 hover:bg-blue-800 cursor-pointer"
                }`}
              >
                {isRegisteringFree ? "Registering..." : continueLabel}
              </button>
            )}
          </div>
        </div>
      </div>

      {payBundleHere && (
        <PayWidgetEvents
          userEmail={user?.email}
          userId={user?.userId}
          paymentAmount={bundlePrice}
          eventId={eventId}
          subscriptionType={["combine"]}
          bundle={bundleChoice}
          onIntellipayReady={setIsIntellipayReady}
        />
      )}
    </>
  );
};

export default CombineBundleStep;
